import { FC, useState, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {useIntl} from 'react-intl'

import { ChatMessageModel, ChatMessageReference, ChatMessageFileModel, ChatMessageRoleType, ChatMessageGenerationType, ChatUploadedFile, TEXT_GENERATION_TYPES, MEDIA_UNDERSTANDING_TYPES } from '../../../models/chat';
import { getDomain, decodeUnicode, isURL, TYPYING_SPEED, determineDirection, removeThinkingPart, getThinkingPart } from '../../../utils';

import { Logo, getLogoAsString } from '../../../components/Logo'
import { ChatResponseFeedbackModal } from './ChatResponseFeedbackModal';
import { ChatResponseAttributionModal } from './ChatResponseAttributionModal';

import { OverlayTrigger, Popover } from 'react-bootstrap'

import { MarkdownWithLinks, convertIslamicTagsIntoMarkdownLinks, renderUserMessage } from '../../../components/MarkdownWithLinks'

import { chatMessageAPI } from '../../../api'

import {MessageGenerating} from '../../../components/MessageGenerating'

import { ChatMessageOptions } from './ChatMessageOptions';
import { ChatMessageOptionResult } from './ChatMessageOptionResult';

import { MenuComponent } from '../../../../_metronic/assets/ts/components'

import { useAuth } from '../../../context/auth'

import UserSymbol from '../../../components/UserPicture'

import UploadedFile from '../../../components/UploadedMediaModal';

import { useLang } from '../../../../_metronic/i18n/Metronici18n';

import clsx from 'clsx';
interface Props {
  chatMessage: ChatMessageModel
  allUploadedFiles?: ChatUploadedFile[]
  updateUploadedFileBase64Url: (uploadedFileId: string, base64Url) => void
  responseToChatMessage: ChatMessageModel | undefined
  setIsTyping: (isTyping:boolean) => void
  withTyping: boolean
  isTyping: boolean
  isSending: boolean
  handleScroll?: () => void
  scrollBottom?: () => void
  handleRegenerate: (responseTo: number, type: ChatMessageGenerationType) => void
  isLast: boolean
  handleOnLoadImage: () => void
}

const UploadedImage_ = ({ generationType, uploadedFile, isSingleImage, loadUploadedFileBase64Url }: { generationType: ChatMessageGenerationType,  uploadedFile: ChatUploadedFile; isSingleImage: boolean; loadUploadedFileBase64Url: () => void}) => {
  const showImage = generationType !== ChatMessageGenerationType.TRANSCRIPTION
  return <div className={clsx({
    'uploaded-image-container': showImage,
    'w-180px': showImage && (!isSingleImage || !uploadedFile.thumbnailBase64Url),
    'flex-center d-flex h-180px bg-gray-200 rounded': showImage && !uploadedFile.thumbnailBase64Url
  })}>
    <UploadedFile
      uploadedFile={uploadedFile}
      loadUploadedFileBase64Url={loadUploadedFileBase64Url}
    />
  </div>
}

const Reference: FC<{ reference: ChatMessageReference, isIslamicRAG: boolean }> = ({ reference, isIslamicRAG }) => {

  const [show, setShow] = useState(false);
  const direction = reference.content && determineDirection(reference.content)
  return <div>
    <OverlayTrigger
      placement="auto"
      show={show}
      rootClose
      overlay={
        <Popover 
          className="fs-8 mw-lg-600px"
          onMouseEnter={() => setShow(true)} 
          onMouseLeave={() => setShow(false)}
        >
          <Popover.Body
            style={{ 
              maxHeight: "250px", 
              overflowY: "auto", 
              whiteSpace: "pre-wrap", 
              ...(direction ? { direction: direction } : {}) 
            }}
          >
            {reference.content && (
              <div className={`mt-2 ${isIslamicRAG && 'quran-reference-popover'}`} style={direction ? { direction: direction } : {}} dangerouslySetInnerHTML={{ __html: decodeUnicode(reference.content).replace(/\n/g, "<br />") }} />
            )}
          </Popover.Body>
        </Popover>
      }
    >
      {isURL(reference.source) ?
        <a
          className={`fs-6`}
          href={reference.source}
          target="_blank"
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
          >
          <i className="fa-solid fa-link rotate-45 source-icon-color" />&nbsp;&nbsp;{getDomain(reference.source)}
        </a>
        :
        <span 
          className="fs-6"
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
        >
          <i className="fa-solid fa-book source-icon-color"/>&nbsp;&nbsp;{getDomain(reference.source)}
        </span>
      }
    </OverlayTrigger>
  </div>
};

const ChatMessage: FC<Props> = ({ chatMessage, allUploadedFiles, updateUploadedFileBase64Url, responseToChatMessage, withTyping, setIsTyping, isTyping, isSending, handleScroll, scrollBottom, handleRegenerate, isLast, handleOnLoadImage }) => {
  
  const location = useLocation()
  const isSharedChat = location.pathname.includes('/s/')
  const { sharedChatId } = useParams<{ sharedChatId: string}>();

  const intl = useIntl()
  const selectedLang = useLang()
  const hasThinkingTag = !(chatMessage.message ?? '').trim().startsWith('<think>') && (chatMessage.message ?? '').includes('</think>')
  const messageForRendering = convertIslamicTagsIntoMarkdownLinks(decodeUnicode(removeThinkingPart(chatMessage.message)))
  const deepThinkingMessageForRendering = hasThinkingTag ? convertIslamicTagsIntoMarkdownLinks(decodeUnicode(getThinkingPart(chatMessage.message))) : undefined
  const [message, setMessage] = useState( withTyping && isTyping ? "" : messageForRendering)
  const [deepThinkingMessage, setDeepThinkingMessage] = useState( withTyping && isTyping ? "" : deepThinkingMessageForRendering)
  const [deepThinkingMessageShow, setDeepThinkingMessageShow] = useState<boolean>(withTyping && isTyping || isLast)

  const direction = chatMessage.message ? determineDirection(chatMessage.message) : null
  const originalDirection = selectedLang === 'ar' ? 'rtl' : 'ltr'

  const {currentUser} = useAuth()
  const currentSettings = currentUser?.settings

  const [messageReaction, setMessageReaction] = useState<string | undefined>()
  const [imageReaction, setImageReaction] = useState<string | undefined>()
  const [isRegenerated, setIsRegenerated] = useState<boolean>(false)

  const [audioAutoPlay, setAudioAutoPlay] = useState<boolean>(isLast)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false)
  const [showAttributionModal, setShowAttributionModal] = useState<boolean>(false)
  const [handlingReaction, setHandlingReaction] = useState<boolean>(false)
  const [feedbackModelType, setFeedbackModelType] = useState<string>(chatMessage.generation_type)
  const [feedbackFileId, setFeedbackFileId] = useState<number>()

  const [translatedMessage, setTranslatedMessage] = useState<string | undefined>(chatMessage.translated_message)
  const [hasImage, setHasImage] = useState<boolean>(chatMessage.role == ChatMessageRoleType.ASSISTANT && chatMessage.generation_type === ChatMessageGenerationType.IMAGE) || (chatMessage.generation_type !== ChatMessageGenerationType.IMAGE && chatMessage.has_image)
  const [generatedImageChatMessageFile, setGeneratedImageChatMessafeFile] = useState<ChatMessageFileModel | undefined>()
  const [generatedAudioChatMessageFile, setGeneratedAudioChatMessafeFile] = useState<ChatMessageFileModel | undefined>()
  const [searchedImages, setSearchedImages] = useState<[] | undefined>()
  const [diacritizedMessage, setDiacritizedMessage] = useState<string | undefined>()

  const [uploadedFiles, setUploadedFiles] = useState<ChatUploadedFile[]>(
    chatMessage.chat_uploaded_files?.length
      ? chatMessage.chat_uploaded_files
      : (chatMessage.chat_uploaded_file_ids?.map((fileId: string) => ({ id: fileId })) || [])
  )

  useEffect(() => {
    MenuComponent.reinitialization()
  }, [])

  useEffect(() => {
    if( chatMessage.role === ChatMessageRoleType.USER && MEDIA_UNDERSTANDING_TYPES.includes(chatMessage.generation_type) ){
      allUploadedFiles?.filter(uploadedFile => uploadedFile.id && chatMessage.chat_uploaded_file_ids?.includes(uploadedFile.id)).forEach( uploadedFile => {
        setUploadedFiles(prev => {
          return prev?.map((file) => {
            if (file.id === uploadedFile.id) {
              return uploadedFile;
            }
            return file;
          });
        })
      })
    }
  }, [allUploadedFiles])

  useEffect(() => {
    setMessageReaction(chatMessage.reaction)
    setIsRegenerated(chatMessage.is_regenerated != undefined && chatMessage.is_regenerated)
    if( TEXT_GENERATION_TYPES.includes(chatMessage.generation_type) ){
      if( chatMessage.id &&  chatMessage.has_image ){
        getGeneratedFile(chatMessage.id, ChatMessageGenerationType.IMAGE)
      }
    }else if( chatMessage.role == ChatMessageRoleType.ASSISTANT && chatMessage.response_to ){
      getGeneratedFile(chatMessage.response_to, chatMessage.generation_type)
    }
  }, [chatMessage])

  useEffect(() => {
    if( generatedImageChatMessageFile ){
      !hasImage && setHasImage(true)
      setImageReaction(chatMessage.generation_type === ChatMessageGenerationType.IMAGE ? chatMessage.reaction : generatedImageChatMessageFile.reaction)
    }
  }, [generatedImageChatMessageFile])


  useEffect(() => {
    let startTime = Date.now();
    let index = 0;
    let jumpedIndex = 0;
    let timeoutId: NodeJS.Timeout | null = null;

    const write = (message: string, setMessage_: (message: string) => void): Promise<void> => {
      return new Promise((resolve) => {
        const typeEffect = () => {
          const elapsedTime = Date.now() - startTime;
          let expectedIndex = Math.floor(elapsedTime / TYPYING_SPEED) + jumpedIndex;

          if (expectedIndex > index) {
            index = expectedIndex;
            setMessage_(message.slice(0, index));
          }

          if (index < message.length) {
            let substring = message.slice(0, index);
            const patterns = [
              ['<', '>'],
              ['](', ')']
            ];
            
            for (const [openPattern, closeChar] of patterns) {
              let openingIndex = substring.lastIndexOf(openPattern);
              if (openingIndex !== -1) {
                let closingIndex = substring.indexOf(closeChar, openingIndex);
                if (closingIndex === -1) {
                  let jumpTo = message.indexOf(closeChar, index);
                  if (jumpTo !== -1) {
                    jumpedIndex += jumpTo - index;
                  }
                }
              }
            }
            
            timeoutId = setTimeout(typeEffect, TYPYING_SPEED);
          } else {
            setMessage_(message);
            resolve(); // signal that typing is done
          }
        };

        timeoutId = setTimeout(typeEffect, TYPYING_SPEED);
      });
    };

    const runTyping = async () => {
      startTime = Date.now();
      index = 0;
      jumpedIndex = 0;

      if (TEXT_GENERATION_TYPES.includes(chatMessage.generation_type)) {
        if (handleScroll) {
          scrollIntervalRef.current = setInterval(handleScroll, 100);
        }

        deepThinkingMessageForRendering && await write(deepThinkingMessageForRendering, setDeepThinkingMessage);
        startTime = Date.now();
        index = 0;
        jumpedIndex = 0;
        await write(messageForRendering, setMessage);
        setIsTyping(false);
      }
    };

    if (withTyping && isTyping) {
      runTyping();
    } else {
      deepThinkingMessageForRendering && setDeepThinkingMessage(deepThinkingMessageForRendering);
      setMessage(messageForRendering);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
    };
    
  }, [withTyping])

  const handleReactionOnRegeneratedResponse = async (reaction: string) => {
    if( chatMessage.id ){
      chatMessageAPI.react(chatMessage.chat_id, chatMessage.id, reaction).then( () => {
        setIsRegenerated(false)
      })
    }
  }

  const handleRegenerateMessage = () => {
    if( chatMessage.response_to){
      handleRegenerate(chatMessage.response_to, chatMessage.generation_type)
    }
  }

  const onLoadGeneratedImage = () => {
    if( isLast && scrollBottom){
      scrollBottom()
    }
    handleOnLoadImage()
  }

  const emptyGeneratedFile = (type) => {
    if( type === ChatMessageGenerationType.IMAGE ){
      setGeneratedImageChatMessafeFile(undefined)
      setHasImage(false)
    }else{
      setGeneratedAudioChatMessafeFile(undefined)
    }
  }

  const setGeneratedFile = (chatMessageFile: ChatMessageFileModel, type: string) => {
    const fileUrl = `data:${ type === ChatMessageGenerationType.IMAGE ? 'image/png' : 'audio/wav'};base64,${chatMessageFile.encoded_file}`;
    chatMessageFile.url = fileUrl
    if( type === ChatMessageGenerationType.IMAGE ){
      setGeneratedImageChatMessafeFile(chatMessageFile)
    }else{
      setGeneratedAudioChatMessafeFile(chatMessageFile)
    }
  }

  const getGeneratedFile = async (chatMessageId: number, type: string) => {
    const response = await chatMessageAPI.getFile(sharedChatId ? sharedChatId : chatMessage.chat_id, chatMessageId, type, isSharedChat)
    if (response.status === 200) {
      setGeneratedFile(response.data.data, type)
    }
  }

  const downloadFile = (url: string, type: string) => {
    const a = document.createElement('a');
    a.href = url;
    if( type === ChatMessageGenerationType.IMAGE ){
      a.download = 'image.png';
    }else{
      a.download = `${type}.wav`;
    }
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  const handleReaction = async (reaction_: string, type: string) => {
    if( !handlingReaction && chatMessage.id ){
      setHandlingReaction(true)
      if( type !== ChatMessageGenerationType.IMAGE ){
        setMessageReaction(messageReaction === reaction_ ? undefined : reaction_)
      }
      let currentFileId: number | undefined = undefined
      let currentReaction: string | undefined = messageReaction
      if( type === ChatMessageGenerationType.IMAGE ){
        currentFileId = generatedImageChatMessageFile?.id
        if( chatMessage.generation_type === ChatMessageGenerationType.IMAGE ){
          setMessageReaction(messageReaction === reaction_ ? undefined : reaction_)
        }else{
          setImageReaction(imageReaction === reaction_ ? undefined : reaction_)
          currentReaction = imageReaction
        }
      }else if ( !TEXT_GENERATION_TYPES.includes(type as ChatMessageGenerationType) ){
        currentFileId = generatedAudioChatMessageFile?.id
      }
      if( reaction_ != currentReaction && reaction_ === 'dislike' ){
        setFeedbackFileId(currentFileId)
        setFeedbackModelType(type)
        setShowFeedbackModal(true)
      }
      chatMessageAPI.react(chatMessage.chat_id, chatMessage.id, reaction_, undefined, currentFileId).then( () => {
        setHandlingReaction(false)
      })
    }
  }


  return (
    <>
      <div className="d-flex flex-row px-0 px-md-2 rounded chat-message fs-4">
        <div className="d-flex">
          <div className="symbol symbol-20px symbol-circle w-30px text-center me-2">
            {chatMessage.role == ChatMessageRoleType.ASSISTANT ? (
              <Logo size={25} />
            ) : (
              <UserSymbol picture={currentUser?.picture}/>
            )}
          </div>
        </div>
        <div className="d-flex flex-column w-100" style={{minWidth:0}}>
          { chatMessage.role == ChatMessageRoleType.USER && MEDIA_UNDERSTANDING_TYPES.includes(chatMessage.generation_type) && (
            <div className={`d-flex flex-row overflow-x-auto ${chatMessage.generation_type !== ChatMessageGenerationType.TRANSCRIPTION && 'mb-2'} gap-3`}>
              {uploadedFiles && uploadedFiles.map((uploadedFile, index) => (
                  <UploadedImage_
                    generationType={chatMessage.generation_type}
                    uploadedFile={uploadedFile}
                    isSingleImage={uploadedFiles.length === 1}
                    key={`chat-message-uploaded-file-${chatMessage.id}-${index}`}
                    loadUploadedFileBase64Url={async () => {
                      if( uploadedFile.id ){
                        const response = await chatMessageAPI.getUploadedFile(sharedChatId ? sharedChatId : chatMessage.chat_id, uploadedFile.id, isSharedChat);
                        updateUploadedFileBase64Url(uploadedFile.id, response.data.data['encoded_file'])
                      }
                    }}
                  />
                ))
              }
            </div>
          )}
          <div className={`chat-message-text lh-lg ${chatMessage.id && chatMessage.role !== ChatMessageRoleType.USER && !TEXT_GENERATION_TYPES.includes(chatMessage.generation_type)  && 'd-none' } ${ chatMessage.role == ChatMessageRoleType.USER ? '' : 'mb-n4'}`} style={direction ? {direction:direction} : {}}>
            {chatMessage.id || chatMessage.role == ChatMessageRoleType.USER ? (
              chatMessage.is_filtered ? (
                <>
                {chatMessage.role == ChatMessageRoleType.USER && (
                  <MarkdownWithLinks>{message}</MarkdownWithLinks>
                )}
                <MarkdownWithLinks>
                  {intl.formatMessage({
                    id: `chat.message.${
                      chatMessage.role == "user" ? "prompt" : "response"
                    }_filtered`,
                  })}
                </MarkdownWithLinks>
                </>
              ) : (
                <>
                  { chatMessage.role == ChatMessageRoleType.USER ?
                    <>
                      {renderUserMessage(message)}
                    </>
                  :
                    <>
                    {hasThinkingTag ?
                      <div className="bg-thinking rounded p-4 mb-4 text-gray-700">
                        <div className={`d-flex justify-content-between ${!isTyping && 'cursor-pointer'} flex-center`} style={{direction:originalDirection}} onClick={()=>!isTyping && setDeepThinkingMessageShow(!deepThinkingMessageShow)}>
                          <div>
                            <i className="fa-solid fa-lightbulb text-warning fs-6 me-2"></i>
                            <span className="fw-semibold fs-6">{intl.formatMessage({id: 'chat.message.thinking'})}</span>
                          </div>
                          {!isTyping && <i className={`fa-solid fa-chevron-${deepThinkingMessageShow ? 'up' : 'down'} fs-6 me-2`}></i>}
                        </div>
                        { deepThinkingMessageShow && 
                          <MarkdownWithLinks className="mb-n3">{deepThinkingMessage}</MarkdownWithLinks>
                        }
                      </div>
                    :
                      responseToChatMessage && responseToChatMessage.need_deep_thinking && (
                      <div className="bg-thinking rounded p-4 mb-4 text-gray-700" style={{direction:originalDirection}}>
                        <i className="fa-solid fa-lightbulb text-warning fs-6 me-2"></i> 
                        <span className="fw-semibold fs-6">{intl.formatMessage({id: 'chat.message.thinking_not_enabled'})}</span>
                      </div>
                      )
                    }
                    <MarkdownWithLinks 
                      tableClassName={chatMessage.model_id === 'poem' ? 'w-md-75' : ''} 
                      withoutTableBorder={chatMessage.model_id === 'poem'}
                    >
                      {message}
                    </MarkdownWithLinks>
                    </>
                  }
                  {Array.isArray(chatMessage.references) &&
                    chatMessage.references.length > 0 && (
                      <div
                        className={`d-flex flex-column mb-5 ${
                          isTyping && isLast && "d-none"
                        }`}
                      >
                        {chatMessage.references?.map((reference, index) => (
                          <Reference 
                            key={chatMessage.id + '-reference-'+index}
                            reference={reference}
                            isIslamicRAG={chatMessage.model_id === 'islamic_rag'}
                          />
                        ))}
                      </div>
                    )}
                </>
              )
            ) : chatMessage.is_failed ? (
              intl.formatMessage({ id: "chat.message.failed" })
            ) : (
              <MessageGenerating />
            )}
          </div>
          <ChatMessageOptionResult 
            isSending={isSending}
            messageRole={chatMessage.role}
            generationType={chatMessage.generation_type}
            diacritizedMessage={diacritizedMessage}
            translatedMessage={translatedMessage}
            hasImage={hasImage && chatMessage.is_failed !== true && chatMessage.is_filtered !== true}
            generatedImageUrl={generatedImageChatMessageFile?.url}
            generatedAudioUrl={generatedAudioChatMessageFile?.url}
            handleReaction={handleReaction}
            imageReaction={chatMessage.generation_type === ChatMessageGenerationType.IMAGE ? messageReaction : imageReaction}
            downloadFile={downloadFile}
            audioAutoPlay={audioAutoPlay}
            audioRef={audioRef}
            searchedImages={searchedImages}
            onLoadGeneratedImage={onLoadGeneratedImage}
          />
          <div className={`min-h-35px mt-2`}>

          {!isSharedChat &&
            <>
            <ChatMessageOptions 
              chatMessage={chatMessage}
              setIsTyping={setIsTyping}
              withTyping={withTyping}
              isTyping={isTyping}
              handleRegenerate={handleRegenerateMessage}
              isLast={isLast}
              handleReaction={handleReaction}
              messageReaction={messageReaction}
              emptyGeneratedFile={emptyGeneratedFile}
              setGeneratedFile={setGeneratedFile}
              setAudioAutoPlay={setAudioAutoPlay}
              setSearchedImages={setSearchedImages}
              setDiacritizedMessage={setDiacritizedMessage}
              setShowAttributionModal={setShowAttributionModal}
              audioRef={audioRef}
              translatedMessage={translatedMessage}
              setTranslatedMessage={setTranslatedMessage}
              generatedAudioUrl={generatedAudioChatMessageFile?.url}
              downloadFile={downloadFile}
            />
            {isLast &&
              chatMessage.id &&
              chatMessage.role == ChatMessageRoleType.ASSISTANT &&
              currentSettings?.response_setting?.reaction &&
              !withTyping &&
              !isTyping &&
              isRegenerated && (
                <div className="alert alert-dismissible bg-opacity-50 bg-secondary chat-message-review-options d-flex flex-column w-300px w-md-450px flex-sm-row p-5 mb-10 mt-2">
                  <div className="d-flex flex-column flex-lg-row">
                    <div className="fw-semibold ">
                      {intl.formatMessage({
                        id: "chat.message.regenerate.review.title",
                      })}
                    </div>
                    <div className="d-flex flex-row mt-lg-0 mt-5">
                      <div
                        className="text-center me-7 cursor-pointer review-btn text-hover-dark"
                        onClick={() => handleReactionOnRegeneratedResponse("better")}
                      >
                        <i className="fa-solid fa-thumbs-up fs-2"></i>
                        <div className="">
                          {intl.formatMessage({
                            id: "chat.message.regenerate.review.better",
                          })}
                        </div>
                      </div>
                      <div
                        className="text-center me-7 cursor-pointer review-btn text-hover-dark"
                        onClick={() => handleReactionOnRegeneratedResponse("worse")}
                      >
                        <i className="fa-solid fa-thumbs-down fs-2"></i>
                        <div className="">
                          {intl.formatMessage({
                            id: "chat.message.regenerate.review.worse",
                          })}
                        </div>
                      </div>
                      <div
                        className="text-center me-7 cursor-pointer review-btn text-hover-dark"
                        onClick={() => handleReactionOnRegeneratedResponse("same")}
                      >
                        <i className="fa-solid fa-equals fs-2"></i>
                        <div className="">
                          {intl.formatMessage({
                            id: "chat.message.regenerate.review.same",
                          })}
                        </div>
                      </div>
                      <div
                        className="me-7 flex-center cursor-pointer review-btn text-hover-dark"
                        onClick={() => handleReactionOnRegeneratedResponse("close")}
                      >
                        <i className="fa-solid fa-xmark fs-4" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              </>
            }
          </div>
        </div>
        {chatMessage.id && showFeedbackModal && (
          <>
            <ChatResponseFeedbackModal
              chatId={chatMessage.chat_id}
              chatMessageId={chatMessage.id}
              fileId={feedbackFileId}
              type={feedbackModelType}
              show={showFeedbackModal}
              handleClose={() => setShowFeedbackModal(false)}
            />
          </>
        )}
        <ChatResponseAttributionModal
          chatMessageResponse={chatMessage}
          show={showAttributionModal}
          handleClose={() => setShowAttributionModal(false)}
        />
      </div>
    </>
  );
};

export { ChatMessage };